import { useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { useCallback } from "react";
import { useRouter } from "solito/router";

import { IssueType, Participant, gql } from "@medbillai/graphql-types";

import { isWeb } from "@medbillai/ui";

import { Flag } from "@medbillai/utils/flag";
import {
  caseIntakeFlowErrorUrl,
  issuePreviewUrl,
  issueUrl,
} from "@medbillai/utils/urls";

import type { UploadFile } from "../../../provider/documents";
import { openIssueMutation } from "../../../screens/cases/utils/queries";
import type { DocumentInputs } from "../../../screens/flows/case-intake/upload-documents/types";
import { createPersonMutation } from "../../../screens/person/utils/mutations";
import { analyticsService } from "../../analytics";
import { errorHandling } from "../../apollo/utils";
import { useBoolVariation } from "../../flag";
import { SingularEvents, singularService } from "../../singular";
import { useCaseIntakeFlowStore } from "./caseIntakeStore";
import {
  type BenefitsQuestionType,
  type BillDisputeType,
  type CaseType,
  type FlowData,
  benefitsQuestionsMessageMap,
  billDisputeMessageMap,
  caseTypeMessageMap,
  caseTypeTitleMap,
} from "./constants";

const sendOpeningMessageMutation = gql(/* GraphQL */ `
  mutation SendOpeningMessage($input: CreateIssueChatEventInput!) {
    # TODO - ENG-198 - move to a fragment
    createIssueChatEvent(input: $input) {
      __typename
      id
    }
  }
`);

const createMultiFileDocumentMutation = gql(/* GraphQL */ `
  mutation CreateMultiFileDocument($input: CreateMultiFileDocumentInput!) {
    createMultiFileDocument(input: $input) {
      document {
        id
      }
      files {
        s3Key
        url
        name
      }
    }
  }
`);

const finalizeDocumentMutation = gql(/* GraphQL */ `
  mutation FinalizeDocument($input: FinalizeDocumentInput!) {
    finalizeDocument(input: $input) {
      document {
        id
      }
    }
  }
`);

// TODO see if we can clean this up and remove the need for this
type DocumentInputKeys = keyof DocumentInputs;

export const documentInputKeys: Set<string> = new Set<DocumentInputKeys>([
  "insuranceCard",
  "explanationOfBenefits",
  "superBill",
  "paymentReceipts",
  "planEnrollmentDocs",
  "denialLetter",
  "screenshots",
  "collectionsLetter",
  "originalBill",
  "otherDocuments",
]);

const canSkipPerson: CaseType[] = ["APP_ISSUE"];

function isDocumentInputKey(key: string): key is DocumentInputKeys {
  // Check if the key exists in the set
  return documentInputKeys.has(key);
}

const generateCaseTitle = (flowData: FlowData) => {
  if ("caseType" in flowData) {
    const caseType = flowData["caseType"] as CaseType;

    return caseTypeTitleMap[caseType];
  } else {
    throw new Error(
      "cannot create case title: caseType not found in flow data",
    );
  }
};

const generateCaseMessage = (flowData: FlowData) => {
  let message: string | undefined;
  if ("caseType" in flowData) {
    const caseType = flowData["caseType"] as CaseType;

    const messageBase = caseTypeMessageMap[flowData["caseType"] as CaseType];

    let messageSuffix: string | undefined;

    if (caseType === "BILL_ISSUE") {
      messageSuffix =
        billDisputeMessageMap[flowData["billIssue"] as BillDisputeType];
    }

    if (caseType === "BENEFITS_QUESTION") {
      messageSuffix =
        benefitsQuestionsMessageMap[
          flowData["benefitsQuestion"] as BenefitsQuestionType
        ];
    }

    if (messageBase && messageSuffix) {
      message = `${messageBase}${messageSuffix}`;
    } else {
      message = messageBase;
    }
  } else {
    throw new Error("cannot create case: caseType not found in flow data");
  }

  return message;
};

// TODO this should probably be moved into a submit screen where we can show
// loading states and handle errors.
export const useSubmit = () => {
  const { replace } = useRouter();

  const reset = useCaseIntakeFlowStore(state => state.reset);
  const updateFlowData = useCaseIntakeFlowStore(state => state.updateFlowData);

  const CASE_INTAKE_V1_PHASE_2 = useBoolVariation(
    Flag.CASE_INTAKE_V1_PHASE_2,
    false,
  );

  const [createPerson] = useMutation(createPersonMutation, {
    context: errorHandling("error-screen"),
    ignoreResults: true,
  });

  const [openIssue] = useMutation(openIssueMutation, {
    context: errorHandling("caller"),
    ignoreResults: true,
  });

  const [sendOpeningMessage] = useMutation(sendOpeningMessageMutation, {
    context: errorHandling("caller"),
    ignoreResults: true,
  });

  const [createMultiFileDocument] = useMutation(
    createMultiFileDocumentMutation,
    {
      context: errorHandling("caller"),
      ignoreResults: true,
    },
  );
  const [finalizeDocument] = useMutation(finalizeDocumentMutation, {
    context: errorHandling("caller"),
    ignoreResults: true,
  });

  const uploadDocument = useCallback(
    async (
      documentLabel: string,
      uploadFiles: UploadFile[],
      issueId: string,
    ) => {
      const { data } = await createMultiFileDocument({
        variables: {
          input: {
            // Guaranteed with the above check; default to first file name.
            name: uploadFiles[0]!.name,
            files: uploadFiles.map(file => ({
              name: file.name,
              sizeBytes: file.sizeBytes,
              contentType: file.contentType,
            })),
            participant: Participant.USER,
          },
        },
      });

      if (!data || !data.createMultiFileDocument) {
        throw new Error("Unexpected empty document from server");
      }

      const { document, files } = data.createMultiFileDocument;

      for (const file of files) {
        // We know we'll find the file, as we just created it. It would have
        // errored out before if it didn't resolve.
        const uploadFile = uploadFiles.find(f => f.name === file.name);
        await fetch(file.url, {
          method: "PUT",
          body: uploadFile!.blob,
          headers: {
            "Content-Type": uploadFile!.contentType,
          },
        });
      }

      await finalizeDocument({
        variables: {
          input: {
            documentId: document.id,
            issueId,
            documentLabel: documentLabel,
            caseIntake: true,
          },
        },
      });
    },
    [createMultiFileDocument, finalizeDocument],
  );

  const setSubmitStage = useCaseIntakeFlowStore(state => state.setSubmitStage);

  const submit = useCallback(
    async (flowData: FlowData) => {
      const start_time = dayjs().unix();

      try {
        const message = generateCaseMessage(flowData);
        const title = generateCaseTitle(flowData);

        if (!title) {
          throw new Error("cannot create case: title cannot be constructed");
        }

        setSubmitStage("OPENING_CASE");

        analyticsService.trackEvent("CreateIssue", { title });
        singularService.event(SingularEvents.createCase);

        if (CASE_INTAKE_V1_PHASE_2) {
          // create person or use selected id
          let personId: string | undefined;

          // look at person fields as personId is set to new-person on new person
          // selection.  This is also a reasonable guard because they are required fields
          if (
            flowData["givenName"] &&
            flowData["familyName"] &&
            flowData["personDob"]
          ) {
            const personResult = await createPerson({
              variables: {
                input: {
                  givenNames: flowData["givenName"]
                    ? [flowData["givenName"] as string]
                    : undefined,
                  familyName: flowData["familyName"] as string,
                  dob: flowData["personDob"] as string,
                },
              },
            });

            if (personResult.data?.createPerson?.__typename !== "Person_Type") {
              throw new Error(
                personResult.data?.createPerson?.message ??
                  `Error creating person: ${JSON.stringify(personResult.data?.createPerson)}`,
              );
            }

            personId = personResult.data?.createPerson?.id;

            if (!personId) {
              throw new Error("failed to create person");
            }
          } else {
            personId = flowData["personId"] as string;

            if (
              !personId &&
              !canSkipPerson.includes(flowData["caseType"]! as CaseType)
            ) {
              throw new Error("person id not found in flow data");
            }
          }

          // create issue
          const { data: issue } = await openIssue({
            variables: {
              input: {
                title,
                issueType: IssueType.OTHER,
                additionalDetails: flowData["additionalDetails"] as
                  | string
                  | undefined,
                personId,
              },
            },
          });

          if (!issue) {
            throw new Error("failed to open new case");
          }

          const latestFlowData = updateFlowData({
            issueId: issue.openNewIssue?.id,
          });

          if (latestFlowData && "issueId" in latestFlowData) {
            setSubmitStage("UPLOADING_DOCUMENTS");

            // upload documents
            for (const [key, value] of Object.entries(latestFlowData)) {
              if (isDocumentInputKey(key) && value) {
                await uploadDocument(
                  key,
                  value as UploadFile[],
                  latestFlowData.issueId as string,
                );
              }
            }

            setSubmitStage("FINISHING_UP");
            // Send initial message
            if (message) {
              await sendOpeningMessage({
                variables: {
                  input: {
                    issueId: latestFlowData.issueId as string,
                    chatBody: message,
                  },
                },
              });
            }
          }

          // go to case screen
          if (latestFlowData && "issueId" in latestFlowData) {
            const url = issueUrl(latestFlowData.issueId as string, true);

            analyticsService.trackEvent("CaseIntakeSubmissionDuration", {
              duration: dayjs().unix() - start_time,
            });

            analyticsService.trackEvent("CaseIntakeSubmissionComplete", {});
            if (isWeb) {
              replace(url);
            } else {
              replace(url, undefined, {
                experimental: {
                  nativeBehavior: "stack-replace",
                  isNestedNavigator: true,
                },
              });
            }
            reset();
          }
        } else {
          const url = issuePreviewUrl(IssueType.OTHER, message, title);

          analyticsService.trackEvent("CaseIntakeSubmissionDuration", {
            duration: dayjs().unix() - start_time,
          });

          analyticsService.trackEvent("CaseIntakeSubmissionComplete", {});

          if (isWeb) {
            replace(url);
          } else {
            replace(url, undefined, {
              experimental: {
                nativeBehavior: "stack-replace",
                isNestedNavigator: true,
              },
            });
          }
          reset();
        }
      } catch (error) {
        analyticsService.trackEvent("CaseIntakeSubmissionError", {});
        console.error("error submitting case", error);
        replace(caseIntakeFlowErrorUrl());
      }
    },
    [
      CASE_INTAKE_V1_PHASE_2,
      createPerson,
      openIssue,
      replace,
      reset,
      sendOpeningMessage,
      setSubmitStage,
      updateFlowData,
      uploadDocument,
    ],
  );

  return { submit };
};
